import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Register.css';
import { useLanguage } from '../contexts/LanguageContext';
import { validatePassword } from '../utils';
import { Button } from './shared';

// Issue #368: sessionStorage key for persisting registration draft across navigation
// to the Privacy Policy / AGB pages. Passwords are intentionally NOT persisted.
const REGISTER_DRAFT_KEY = 'registerFormDraft';

const loadRegisterDraft = () => {
  try {
    const saved = sessionStorage.getItem(REGISTER_DRAFT_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    return null;
  }
};

const Register = ({ onNavigateToLogin, onNavigateToResendVerification, onNavigateToPrivacyPolicy, onNavigateToTermsAndConditions }) => {
  const { t, language } = useLanguage(); // Issue #248: Get current language for registration email
  // Issue #368: Restore non-sensitive draft fields so user input survives navigation
  // to the Privacy Policy or AGB page. Passwords are deliberately excluded.
  const [formData, setFormData] = useState(() => {
    const draft = loadRegisterDraft();
    return {
      firstName: draft?.firstName || '',
      lastName: draft?.lastName || '',
      password: '',
      passwordRepeat: '',
      email: draft?.email || ''
    };
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  // Issue #380: Show dedicated error screen when verification email fails to send immediately.
  // Cleared again when a subsequent resend succeeds, so the user lands on the normal screen.
  const [emailSendFailed, setEmailSendFailed] = useState(false);
  const [isResendingAfterFailure, setIsResendingAfterFailure] = useState(false);
  // Issue #235: AGB acceptance state - Issue #368: also restored from draft
  const [agbAccepted, setAgbAccepted] = useState(() => loadRegisterDraft()?.agbAccepted === true);

  // Issue #368: Persist non-sensitive form fields so they survive a round-trip
  // to the Privacy Policy / AGB page (component unmounts on navigation).
  useEffect(() => {
    try {
      sessionStorage.setItem(REGISTER_DRAFT_KEY, JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        agbAccepted
      }));
    } catch (e) {
      // sessionStorage unavailable or quota exceeded — fall through silently.
    }
  }, [formData.firstName, formData.lastName, formData.email, agbAccepted]);

  // Issue #85: Email domain validation state
  const [emailDomainValid, setEmailDomainValid] = useState(null);
  const [emailDomainMessage, setEmailDomainMessage] = useState('');
  const [validUniversityName, setValidUniversityName] = useState('');
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);

  // Issue #85: Debounced email validation effect
  useEffect(() => {
    const email = formData.email;

    // Reset if email is empty or incomplete
    if (!email || !email.includes('@') || email.split('@')[1]?.length < 3) {
      setEmailDomainValid(null);
      setEmailDomainMessage('');
      return;
    }

    // Basic format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailDomainValid(null);
      setEmailDomainMessage('');
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsValidatingEmail(true);
      try {
        const response = await axios.post('/api/validate-email-domain', { email });
        if (response.data.valid && response.data.university_name) {
          setEmailDomainValid(true);
          setValidUniversityName(response.data.university_name);
          setEmailDomainMessage('valid');
        } else {
          setEmailDomainValid(false);
          setValidUniversityName('');
          setEmailDomainMessage(t('auth.invalidEmail'));
        }
      } catch (error) {
        setEmailDomainValid(null);
        setEmailDomainMessage('');
      } finally {
        setIsValidatingEmail(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [formData.email]);

  // Issue #171: Validate all fields when email domain validation completes
  // This ensures users see all validation errors before focusing on the submit button
  useEffect(() => {
    if (emailDomainValid !== null && !isValidatingEmail) {
      const newErrors = {};

      // Validate first name
      if (!formData.firstName.trim()) {
        newErrors.firstName = t('auth.firstNameRequired');
      }

      // Validate last name
      if (!formData.lastName.trim()) {
        newErrors.lastName = t('auth.lastNameRequired');
      }

      // Validate password strength
      if (formData.password && !validatePassword(formData.password)) {
        newErrors.password = t('auth.weakPassword');
      }

      // Validate password match
      if (formData.passwordRepeat && formData.password !== formData.passwordRepeat) {
        newErrors.passwordRepeat = t('auth.passwordMismatch');
      }

      setErrors(prev => ({ ...prev, ...newErrors }));
    }
  }, [emailDomainValid, isValidatingEmail, formData.firstName, formData.lastName, formData.password, formData.passwordRepeat, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return false;
    }

    // Check university domain whitelist
    // Issue #332: Beta testing - only TU Hamburg and HAW Kiel students allowed
    const universityDomains = [
      // TU Hamburg
      'tuhh.de',                        // TU Hamburg

      // HAW Kiel (students only)
      'stu.haw-kiel.de',                // HAW Kiel (current student domain)
      'student.fh-kiel.de',             // HAW Kiel (legacy student domain)

      // Testing
      'universe-app.online'             // For testing purposes
    ];

    return universityDomains.some(domain => email.toLowerCase().endsWith(`@${domain}`));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('auth.firstNameRequired');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('auth.lastNameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('auth.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('auth.invalidEmail');
    }

    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    } else if (!validatePassword(formData.password)) {
      newErrors.password = t('auth.weakPassword');
    }

    if (!formData.passwordRepeat) {
      newErrors.passwordRepeat = t('auth.repeatPasswordRequired');
    } else if (formData.password !== formData.passwordRepeat) {
      newErrors.passwordRepeat = t('auth.passwordMismatch');
    }

    // Issue #235: AGB acceptance is mandatory
    if (!agbAccepted) {
      newErrors.agbAccepted = t('auth.agbRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await axios.post('/api/register', {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        confirm_password: formData.passwordRepeat,
        agb_accepted: agbAccepted, // Issue #235
        language: language // Issue #248: Send language for verification email
      });

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setRegisteredEmail(formData.email);
        // Issue #380: Backend signals immediate verification-email failure with email_send_failed.
        // When true, render the dedicated error screen instead of the normal "Alles klar" screen.
        setEmailSendFailed(response.data.email_send_failed === true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          password: '',
          passwordRepeat: '',
          email: ''
        });
        setAgbAccepted(false); // Issue #235
        // Issue #368: Clear persisted draft once the account has been created.
        try { sessionStorage.removeItem(REGISTER_DRAFT_KEY); } catch (e) {}
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const { message, field } = error.response.data;
        if (field) {
          setErrors({ [field]: message });
        } else {
          setErrors({ general: message });
        }
      } else {
        setErrors({ general: t('auth.systemUnavailable') });
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  // Issue #380: Retry sending the verification email from the immediate-error screen.
  // On success, swap to the normal "Alles klar" screen; on failure, stay on the error screen.
  const handleRetryResend = async () => {
    if (!registeredEmail || isResendingAfterFailure) return;

    setIsResendingAfterFailure(true);
    setErrors({});
    try {
      const response = await axios.post('/api/resend-verification', {
        email: registeredEmail,
      });
      if (response.data.success && response.data.email_send_failed !== true) {
        setEmailSendFailed(false);
      } else {
        setEmailSendFailed(true);
      }
    } catch (error) {
      // Network/server errors keep the user on the error screen so they can try again.
      setEmailSendFailed(true);
      if (error.response && error.response.data && error.response.data.message) {
        setErrors({ general: error.response.data.message });
      }
    } finally {
      setIsResendingAfterFailure(false);
    }
  };

  const isFormValid = () => {
    // Button should be enabled if all fields are filled AND email domain is valid
    // Issue #85: Require valid university email domain
    // Issue #171: Also require valid password and matching passwords
    // Issue #235: Require AGB acceptance
    return formData.firstName.trim() &&
           formData.lastName.trim() &&
           formData.email.trim() &&
           formData.password &&
           formData.passwordRepeat &&
           validatePassword(formData.password) &&
           formData.password === formData.passwordRepeat &&
           emailDomainValid === true &&
           agbAccepted === true;
  };

  // Issue #380: Immediate verification-email error screen.
  // Shown when the backend reports email_send_failed=true on registration (or after a failed retry).
  // The user can hit "Link erneut senden" to retry; on success we fall through to the normal screen.
  if (successMessage && emailSendFailed) {
    return (
      <div className="register-container">
        <header className="navigation-bar" role="banner">
          <h1 className="page-title">{t('auth.createAccount')}</h1>
        </header>

        <main id="main-content" className="verification-sent-content" role="main">
          <div
            className="verification-immediate-error-message text-pretty"
            role="alert"
            aria-live="assertive"
          >
            {t('auth.verificationEmailImmediateErrorPrefix')}
            <strong className="verification-immediate-error-email">
              {t('auth.customerServiceEmail')}
            </strong>
            {t('auth.verificationEmailImmediateErrorSuffix')}
          </div>

          <div className="verification-sent-button-wrapper">
            <Button
              variant="primary"
              fullWidth
              onClick={handleRetryResend}
              disabled={isResendingAfterFailure}
              aria-busy={isResendingAfterFailure}
            >
              {isResendingAfterFailure
                ? t('auth.sending')
                : t('auth.resendLinkButton')}
            </Button>
          </div>

          {errors.general && (
            <div className="error-message general-error" role="alert" aria-live="polite">
              {errors.general}
            </div>
          )}
        </main>
      </div>
    );
  }

  // Issue #86: Show verification email sent screen after successful registration
  if (successMessage) {
    return (
      <div className="register-container">
        {/* Navigation Bar */}
        <header className="navigation-bar" role="banner">
          <h1 className="page-title">{t('auth.createAccount')}</h1>
        </header>

        {/* Issue #86: Verification Email Sent Screen */}
        <main id="main-content" className="verification-sent-content" role="main">
          <div className="verification-sent-message text-pretty">
            {t('auth.verificationEmailSent')}
          </div>

          <div className="verification-sent-button-wrapper">
            <Button
              variant="primary"
              fullWidth
              onClick={onNavigateToLogin}
            >
              {t('auth.allesKlar')}
            </Button>
          </div>

          {/* Issue #15: Navigate to resend verification page */}
          <div className="verification-resend-link-wrapper">
            <Button
              variant="link"
              onClick={() => onNavigateToResendVerification(registeredEmail)}
              aria-label={t('auth.noLinkReceived')}
            >
              {t('auth.noLinkReceived')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="register-container">
      {/* Skip to main content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>

      {/* Navigation Bar */}
      <header className="navigation-bar" role="banner">
        <h1 className="page-title">{t('auth.createAccount')}</h1>
      </header>

      {/* Main Content */}
      <main id="main-content" className="main-content" role="main">
        <form onSubmit={handleSubmit} aria-label={t('auth.registerForm')}>
          {/* Name Fields */}
          <div className="dual-text-field">
            <div className="text-field">
              <label htmlFor="register-firstName" className="visually-hidden">
                {t('auth.firstName')}
              </label>
              <input
                type="text"
                id="register-firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t('auth.firstName')}
                className={`text-field-input ${errors.firstName ? 'error' : ''}`}
                aria-invalid={errors.firstName ? 'true' : 'false'}
                aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                required
                autoComplete="given-name"
              />
              {errors.firstName && (
                <div id="firstName-error" className="error-message" role="alert">
                  {errors.firstName}
                </div>
              )}
            </div>
            <div className="text-field">
              <label htmlFor="register-lastName" className="visually-hidden">
                {t('auth.lastName')}
              </label>
              <input
                type="text"
                id="register-lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t('auth.lastName')}
                className={`text-field-input ${errors.lastName ? 'error' : ''}`}
                aria-invalid={errors.lastName ? 'true' : 'false'}
                aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                required
                autoComplete="family-name"
              />
              {errors.lastName && (
                <div id="lastName-error" className="error-message" role="alert">
                  {errors.lastName}
                </div>
              )}
            </div>
          </div>

          {/* Password Field */}
          <div className="text-field-wrapper">
            <label htmlFor="register-password" className="visually-hidden">
              {t('auth.password')}
            </label>
            <div className={`text-field-input-wrapper ${errors.password ? 'error' : ''}`}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="register-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.password')}
                className="text-field-input-full"
                aria-invalid={errors.password ? 'true' : 'false'}
                aria-describedby={errors.password ? 'password-error password-help' : 'password-help'}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className={`visibility-toggle ${showPassword ? 'active' : ''}`}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? t('a11y.hidePassword') : t('a11y.showPassword')}
                aria-pressed={showPassword}
              >
                <span aria-hidden="true">👁️</span>
              </button>
            </div>
            {errors.password && (
              <div id="password-error" className="error-message" role="alert">
                {errors.password}
              </div>
            )}
          </div>

          {/* Password Repeat Field */}
          <div className="text-field-wrapper">
            <label htmlFor="register-passwordRepeat" className="visually-hidden">
              {t('auth.repeatPassword')}
            </label>
            <div className={`text-field-input-wrapper ${errors.passwordRepeat ? 'error' : ''}`}>
              <input
                type={showPasswordRepeat ? 'text' : 'password'}
                id="register-passwordRepeat"
                name="passwordRepeat"
                value={formData.passwordRepeat}
                onChange={handleChange}
                placeholder={t('auth.repeatPassword')}
                className="text-field-input-full"
                aria-invalid={errors.passwordRepeat ? 'true' : 'false'}
                aria-describedby={errors.passwordRepeat ? 'passwordRepeat-error' : undefined}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className={`visibility-toggle ${showPasswordRepeat ? 'active' : ''}`}
                onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                aria-label={showPasswordRepeat ? t('auth.hidePasswordRepeat') : t('auth.showPasswordRepeat')}
                aria-pressed={showPasswordRepeat}
              >
                <span aria-hidden="true">👁️</span>
              </button>
            </div>
            {errors.passwordRepeat && (
              <div id="passwordRepeat-error" className="error-message" role="alert">
                {errors.passwordRepeat}
              </div>
            )}
          </div>

          {/* Email Field */}
          {/* Issue #171: Show red border when email domain validation fails */}
          <div className="text-field-wrapper">
            <label htmlFor="register-email" className="visually-hidden">
              {t('auth.studentEmailLabel')}
            </label>
            <input
              type="email"
              id="register-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('auth.studentEmail')}
              className={`text-field-input-full ${errors.email || emailDomainValid === false ? 'error' : ''}`}
              aria-invalid={errors.email || emailDomainValid === false ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error password-help' : (emailDomainValid === false ? 'email-domain-error password-help' : 'password-help')}
              required
              autoComplete="email"
            />
            {errors.email && (
              <div id="email-error" className="error-message" role="alert">
                {errors.email}
              </div>
            )}
            {/* Issue #85: Email Domain Validation Message */}
            {/* Issue #171: Uses error-message class for consistent styling */}
            {emailDomainMessage && !errors.email && (
              <div id="email-domain-error" className={`error-message email-domain-message ${emailDomainValid ? 'valid' : 'invalid'}`} role="status" aria-live="polite">
                {isValidatingEmail ? 'Validiere...' : (
                  emailDomainValid && validUniversityName
                    ? <>Du bist in der <strong>{validUniversityName}</strong> eingeschrieben.</>
                    : emailDomainMessage
                )}
              </div>
            )}
          </div>

          {/* Help Text */}
          <div id="password-help" className="help-text" role="status">
            <svg className="info-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="8" r="1" fill="currentColor"/>
            </svg>
            <span>{t('auth.verificationHelp')}</span>
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="error-message general-error" role="alert" aria-live="polite">
              {errors.general}
            </div>
          )}

          {/* Issue #68: Privacy Policy Link */}
          {/* Issue #171: Text aligns with icon, wraps naturally */}
          <div className="privacy-policy-notice">
            <svg className="info-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="8" r="1" fill="currentColor"/>
            </svg>
            <span className="privacy-policy-text">
              {t('auth.privacyPolicyNotice')}{' '}
              <button
                type="button"
                className="privacy-policy-link"
                onClick={onNavigateToPrivacyPolicy}
              >
                {t('profile.privacyPolicy')}
              </button>
            </span>
          </div>

          {/* Issue #235: AGB Checkbox - below Privacy Policy */}
          {/* Uses shared checkbox-custom styling from accessibility.css */}
          <div className="agb-checkbox-container">
            <label className="agb-checkbox-label">
              <input
                type="checkbox"
                checked={agbAccepted}
                onChange={(e) => {
                  setAgbAccepted(e.target.checked);
                  if (errors.agbAccepted) {
                    setErrors(prev => ({ ...prev, agbAccepted: '' }));
                  }
                }}
                className="visually-hidden"
                aria-label={t('auth.agbCheckboxPrefix') + ' ' + t('auth.agbLinkText') + ' ' + t('auth.agbCheckboxSuffix')}
                aria-describedby={errors.agbAccepted ? 'agb-error' : undefined}
              />
              <span className={`checkbox-custom ${agbAccepted ? 'checked' : ''}`} aria-hidden="true">
                {agbAccepted && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M1 4L3.5 6.5L9 1" stroke="#F5F5F5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span className="checkbox-text">
                {t('auth.agbCheckboxPrefix')}{' '}
                <button
                  type="button"
                  className="agb-link"
                  onClick={onNavigateToTermsAndConditions}
                >
                  {t('auth.agbLinkText')}
                </button>
                {' '}{t('auth.agbCheckboxSuffix')}
              </span>
            </label>
            {errors.agbAccepted && (
              <div id="agb-error" className="error-message agb-error" role="alert">
                {errors.agbAccepted}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="submit-button-wrapper">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isFormValid() || isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? t('auth.registering') : t('auth.createAccount')}
            </Button>
          </div>

          {/* Login Link */}
          {onNavigateToLogin && (
            <div className="login-link-wrapper">
              <span className="login-link-text">
                {t('auth.alreadyHaveAccount')}{' '}
                <button
                  type="button"
                  className="login-link-button"
                  onClick={onNavigateToLogin}
                >
                  {t('auth.loginHere')}
                </button>
              </span>
            </div>
          )}
        </form>
      </main>
    </div>
  );
};

export default Register;
